import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config"
import { PolicyColumnKey } from "@common-shared/config/oneVerify/oneVerifyPoliciesColumns.config"
import {
    IPatientIdentificationData,
    IPolicyColumnConfiguration,
    IPolicyIdentificationData,
} from "@common-shared/types/policies.types"
import {
    IOneVerifyCoverageCheckHistory,
    IOneVerifyCoverageResult,
    IOneVerifyPolicyComment,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { COVERAGE_CONFIG } from "../config/coverage.config"
import { POLICIES_COLUMNS_CONFIG } from "../config/policiesColumns.config"
import { ICoverageBreakdownData } from "../types/data.types"
import { ExportReportHelper } from "./exportReport.helper"
import { UtilHelper } from "./util.helper"

export class CoverageHistoryHelper {
    /**
     * Get policy identification data
     */
    static getPolicyIdentificationData(
        coverageCheck: IOneVerifyCoverageCheckHistory | null,
    ): IPolicyIdentificationData | null {
        const data: Partial<IPolicyIdentificationData> = {
            memberId: coverageCheck?.policy?.memberId?.trim(),
            memberDob: UtilHelper.getValidatedMysqlDateString(
                coverageCheck?.policy?.memberDob,
            ),
            modality: coverageCheck?.policy?.modality,
            modalityCode: coverageCheck?.policy?.modalityCode,
            network:
                coverageCheck?.policy?.network ||
                CoverageHistoryHelper.getCoverageCheckNetwork(coverageCheck),
        }

        // All the values from identification should be set in order for it to be valid
        // Valid data required for example for creating override
        const isValid =
            Object.values(data).length ===
            Object.values(data).filter(value => !!value).length

        return isValid ? (data as IPolicyIdentificationData) : null
    }

    /**
     * Get patient identification data
     */
    static getPatientIdentificationData(
        coverageCheck: IOneVerifyCoverageCheckHistory | null,
    ): IPatientIdentificationData | null {
        const data: Partial<IPatientIdentificationData> = {
            firstName:
                coverageCheck?.coverageResult.demographics?.firstName?.trim(),
            lastName:
                coverageCheck?.coverageResult.demographics?.lastName?.trim(),
            dob: UtilHelper.getValidatedMysqlDateString(
                coverageCheck?.coverageResult.demographics?.dob,
            ),
            type:
                coverageCheck?.coverageResult.customerPatientType ||
                coverageCheck?.requestData.customerPatientType ||
                null,
            practiceCustomId:
                coverageCheck?.coverageResult.customerPatientId ||
                coverageCheck?.requestData.customerPatientId ||
                null,
        }

        // Patient main info needs to be set and not be a fallback value (same as in backend)
        const isValid = [data.firstName, data.lastName, data.dob].every(
            value =>
                !!value &&
                value !==
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
        )

        return isValid ? (data as IPatientIdentificationData) : null
    }

    /**
     * Get member ZIP from a previous check to prefill SmartScan
     * Member's own address first, then subscriber's (dependents often have only the subscriber's)
     */
    static getSmartScanZipCode(
        coverageResult?: IOneVerifyCoverageResult | null,
    ): string {
        return (
            UtilHelper.normalizeZipCode(
                coverageResult?.demographics?.address?.zip,
            ) ||
            UtilHelper.normalizeZipCode(
                coverageResult?.subscriberDemographics?.address?.zip,
            )
        )
    }

    /**
     * Get coverage check network
     * IF undefined, fallback to in-network (default behavior)
     */
    static getCoverageCheckNetwork(
        coverageCheck: IOneVerifyCoverageCheckHistory | null,
    ): PayerCoverageCheckNetwork {
        return (coverageCheck?.coverageResult.isInNetworkCheck ??
            coverageCheck?.requestData.inNetwork) === false
            ? PayerCoverageCheckNetwork.OUT
            : PayerCoverageCheckNetwork.IN
    }

    /**
     * Check if coverage check is related to comment
     */
    static isCommentRelatedCoverageCheck(
        coverageCheck: IOneVerifyCoverageCheckHistory | null,
        comment: IOneVerifyPolicyComment,
    ): boolean {
        if (!coverageCheck) {
            return false
        }

        return !!(
            // compare policy IDs first
            (
                (coverageCheck.policy?.id &&
                    comment.policy?.id &&
                    coverageCheck.policy.id === comment.policy.id) ||
                // if policy generated, compare by modality, memberId, memberDob, network
                (coverageCheck.policy?.isGenerated &&
                    coverageCheck.policy.modality ===
                        comment.policy?.modality &&
                    coverageCheck.policy.memberId === comment.policy.memberId &&
                    coverageCheck.policy.memberDob ===
                        comment.policy.memberDob &&
                    coverageCheck.policy.network === comment.policy.network) ||
                (coverageCheck.policy?.patient &&
                    comment.patient &&
                    coverageCheck.policy.patient.id === comment.patient.id)
            )
        )
    }

    /**
     * Get patient demographic breakdown items
     */
    static getPatientDemographicBreakdownItems(
        coverageCheck: IOneVerifyCoverageCheckHistory,
    ): ICoverageBreakdownData[] {
        const [
            requestedPatientFirstName,
            correctedPatientFirstName,
            requestedPatientLastName,
            correctedPatientLastName,
            requestedPatientDob,
            correctedPatientDob,
            patientAddress,
            patientGender,
            relationshipToSubscriber,
        ] = [
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.REQUESTED_PATIENT_FIRST_NAME
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.CORRECTED_PATIENT_FIRST_NAME
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.REQUESTED_PATIENT_LAST_NAME
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.CORRECTED_PATIENT_LAST_NAME
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.REQUESTED_PATIENT_DOB
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.CORRECTED_PATIENT_DOB
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.PATIENT_ADDRESS
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.PATIENT_GENDER
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.RELATIONSHIP_TO_SUBSCRIBER
            ],
        ].map(
            columnConfig =>
                ExportReportHelper.getMappedColumnValue(
                    coverageCheck,
                    columnConfig,
                    undefined,
                    false,
                ) as string | null,
        )

        return [
            !requestedPatientFirstName &&
            !requestedPatientLastName &&
            !correctedPatientFirstName &&
            !correctedPatientLastName
                ? {
                      label: "Member Name",
                      value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNotProvided,
                  }
                : {
                      label: "Member Name",
                      value: [
                          requestedPatientFirstName,
                          requestedPatientLastName,
                      ]
                          .filter(item => !!item)
                          .join(" ")
                          .trim(),
                      valueDifference: [
                          correctedPatientFirstName ||
                              requestedPatientFirstName ||
                              COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
                          correctedPatientLastName ||
                              requestedPatientLastName ||
                              COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
                      ]
                          .filter(item => !!item)
                          .join(" ")
                          .trim(),
                  },
            !requestedPatientDob && !correctedPatientDob
                ? {
                      label: "DOB",
                      value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNotProvided,
                  }
                : {
                      label: "DOB",
                      value: requestedPatientDob,
                      valueDifference:
                          requestedPatientDob ||
                          correctedPatientDob ||
                          COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
                  },
            {
                label: "Address",
                value:
                    patientAddress ||
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
            {
                label: "Gender",
                value:
                    patientGender ||
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
            relationshipToSubscriber
                ? {
                      label: "Rel. to Subscriber",
                      value: relationshipToSubscriber,
                  }
                : null,
        ].filter(item => !!item)
    }

    /**
     * Get subscriber demographic breakdown items
     */
    static getSubscriberDemographicBreakdownItems(
        coverageCheck: IOneVerifyCoverageCheckHistory,
    ): ICoverageBreakdownData[] {
        const [
            subscriberMemberId,
            subscriberFirstName,
            subscriberLastName,
            subscriberDob,
            subscriberAddress,
            subscriberGender,
        ] = [
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.SUBSCRIBER_MEMBER_ID
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.SUBSCRIBER_FIRST_NAME
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.SUBSCRIBER_LAST_NAME
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.SUBSCRIBER_DOB
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.SUBSCRIBER_ADDRESS
            ],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.SUBSCRIBER_GENDER
            ],
        ].map(
            columnConfig =>
                ExportReportHelper.getMappedColumnValue(
                    coverageCheck,
                    columnConfig,
                    undefined,
                    false,
                ) as string | null,
        )

        return [
            {
                label: "Member ID",
                value:
                    subscriberMemberId ||
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
            !subscriberFirstName && !subscriberLastName
                ? {
                      label: "Member Name",
                      value: COVERAGE_CONFIG.defaultFallbackCoverageDataValueNotProvided,
                  }
                : {
                      label: "Member Name",
                      value: [subscriberFirstName, subscriberLastName]
                          .filter(item => !!item)
                          .join(" ")
                          .trim(),
                  },
            {
                label: "DOB",
                value:
                    subscriberDob ||
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
            {
                label: "Address",
                value:
                    subscriberAddress ||
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
            {
                label: "Gender",
                value:
                    subscriberGender ||
                    COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
            },
        ]
    }

    /**
     * Get provider breakdown items
     */
    static getProviderBreakdownItems(
        coverageCheck: IOneVerifyCoverageCheckHistory,
    ): ICoverageBreakdownData[] {
        const [provider, providerNpi, cptCode, providerSessionCharge] = [
            POLICIES_COLUMNS_CONFIG.columnsConfig[PolicyColumnKey.PROVIDER],
            POLICIES_COLUMNS_CONFIG.columnsConfig[PolicyColumnKey.PROVIDER_NPI],
            POLICIES_COLUMNS_CONFIG.columnsConfig[PolicyColumnKey.CPT_CODE],
            POLICIES_COLUMNS_CONFIG.columnsConfig[
                PolicyColumnKey.PROVIDER_SESSION_CHARGE
            ],
        ].map(
            columnConfig =>
                ExportReportHelper.getMappedColumnValue(
                    coverageCheck,
                    columnConfig,
                    undefined,
                    false,
                ) as string | null,
        )

        return [
            // We're using npi as an alternative source for provider name, but it's redundant here
            provider && provider !== providerNpi
                ? {
                      label: "Name",
                      value: provider,
                  }
                : null,
            providerNpi
                ? {
                      label: "NPI",
                      value: providerNpi,
                  }
                : null,
            cptCode
                ? {
                      label: "CPT Code",
                      value: cptCode,
                  }
                : null,
            providerSessionCharge
                ? {
                      label: "Session Rate",
                      value: providerSessionCharge,
                  }
                : null,
        ].filter(item => !!item)
    }

    /**
     * Get policy detail reset breakdown items
     */
    static getBenefitsResetBreakdownItems(
        coverageCheck: IOneVerifyCoverageCheckHistory,
        columnConfigs: IPolicyColumnConfiguration[],
    ): ICoverageBreakdownData[] {
        return columnConfigs
            .map(columnConfig => {
                return columnConfig.resetBenefitsCompareWith &&
                    columnConfig.resetBenefitsLabelOverride
                    ? {
                          label: columnConfig.resetBenefitsLabelOverride,
                          value: ExportReportHelper.getMappedColumnValue(
                              coverageCheck,
                              POLICIES_COLUMNS_CONFIG.columnsConfig[
                                  columnConfig.resetBenefitsCompareWith
                              ],
                              COVERAGE_CONFIG.defaultFallbackCoverageDataValueNone,
                              false,
                          ) as string | null,
                          valueDifference:
                              (ExportReportHelper.getMappedColumnValue(
                                  coverageCheck,
                                  columnConfig,
                                  COVERAGE_CONFIG.defaultFallbackCoverageDataValueNone,
                                  false,
                              ) as string | null) || undefined,
                      }
                    : null
            })
            .filter(item => !!item)
    }
}
